import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-License-Identifier: AGPL-3.0-or-later
import { useId, useMemo, useState } from 'react';
import { ExtensionUrl } from '@intakeform/fhir';
import { parseAndValidatePhone } from '@intakeform/validation';
import { Input } from '../../primitives/Input.js';
import { describedBy, FieldHost } from '../FieldHost.js';
import { useLanguage } from '../../i18n/LanguageProvider.js';
/**
 * Phone field renderer (in-apm). Mounts when an item has
 * `item.type === 'string'` + `phone-validation=true`. Three pieces:
 *
 *   1. Country picker — drives parsing. Default comes from the
 *      item's `phone-country-bias` extension, the parent's `defaultCountry`
 *      prop, or 'US'.
 *   2. Phone input — free-form. On blur, the value is parsed via
 *      libphonenumber-js and reflowed into international format
 *      (preserves the underlying value the form stores).
 *   3. Inline help line — the international format on success, the
 *      libphonenumber error on failure. Pairs with the FieldHost
 *      `FormError` for the server-side findings.
 *
 * The field stores the *raw* string the patient typed until parse
 * succeeds — at which point it normalises to E.164. That way an
 * in-progress draft round-trips what the patient sees, and a
 * completed submit always carries the canonical form.
 */
/** Short, curated picker list. Custom countries can extend via props. */
export const DEFAULT_COUNTRY_OPTIONS = [
    { code: 'US', label: 'United States (+1)' },
    { code: 'CA', label: 'Canada (+1)' },
    { code: 'GB', label: 'United Kingdom (+44)' },
    { code: 'AU', label: 'Australia (+61)' },
    { code: 'DE', label: 'Germany (+49)' },
    { code: 'FR', label: 'France (+33)' },
    { code: 'IE', label: 'Ireland (+353)' },
    { code: 'IN', label: 'India (+91)' },
    { code: 'MX', label: 'Mexico (+52)' },
    { code: 'NZ', label: 'New Zealand (+64)' },
];
function asString(v) {
    return typeof v === 'string' ? v : '';
}
function resolveDefaultCountry(item, fallback) {
    const fieldBias = item.extension?.find((e) => e.url === ExtensionUrl.phoneCountryBias)?.valueCode;
    return fieldBias ?? fallback ?? 'US';
}
export function Phone(props) {
    const { item, value, errors, disabled, onChange, defaultCountry, countryOptions } = props;
    const { t } = useLanguage();
    const inputId = useId();
    const countryId = useId();
    const helpId = useId();
    const initialCountry = useMemo(() => resolveDefaultCountry(item, defaultCountry), [item, defaultCountry]);
    const [country, setCountry] = useState(initialCountry);
    const [rawValue, setRawValue] = useState(asString(value));
    const [parseResult, setParseResult] = useState(null);
    const options = countryOptions ?? DEFAULT_COUNTRY_OPTIONS;
    const onBlur = (_e) => {
        if (rawValue.trim() === '') {
            setParseResult(null);
            return;
        }
        const result = parseAndValidatePhone(rawValue, country);
        setParseResult(result);
        if (result.ok) {
            // Normalise the stored value to E.164 once parsing succeeds.
            // Reflow the visible input to the international format so the
            // patient sees the result of their entry.
            setRawValue(result.international);
            onChange(result.formatted);
        }
    };
    // Help text reflects the latest parse attempt; never empty so the
    // assistive-tech `aria-describedby` always has a target.
    const helpMessage = parseResult === null
        ? t('fields.phone-hint')
        : parseResult.ok
            ? `${parseResult.international} · ${parseResult.country}${parseResult.type !== 'unknown' ? ` · ${parseResult.type}` : ''}`
            : parseResult.message;
    const helpTone = parseResult === null ? 'neutral' : parseResult.ok ? 'success' : 'error';
    return (_jsx(FieldHost, { item: item, errors: errors, controlId: inputId, children: (ctx) => (_jsxs("div", { "data-phone-field": "", className: "phone-field", children: [_jsxs("div", { className: "phone-field-row", children: [_jsxs("label", { htmlFor: countryId, className: "phone-field-country-label", children: [t('fields.country-label'), _jsx("select", { id: countryId, value: country, disabled: disabled === true, onChange: (e) => {
                                        setCountry(e.currentTarget.value);
                                        // Reparse with the new country if there's a value
                                        // present — gives instant feedback when the user
                                        // realises their country was wrong.
                                        if (rawValue.trim() !== '') {
                                            const next = parseAndValidatePhone(rawValue, e.currentTarget.value);
                                            setParseResult(next);
                                            if (next.ok) {
                                                setRawValue(next.international);
                                                onChange(next.formatted);
                                            }
                                        }
                                    }, children: options.map((o) => (_jsx("option", { value: o.code, children: o.label }, o.code))) })] }), _jsx(Input, { id: inputId, type: "tel", inputMode: "tel", autoComplete: "tel", value: rawValue, disabled: disabled === true, required: item.required === true, "aria-invalid": ctx.hasError || (parseResult !== null && !parseResult.ok) || undefined, "aria-describedby": describedBy(ctx, helpId), onChange: (e) => {
                                setRawValue(e.currentTarget.value);
                                // Clear the last parse result so stale messaging
                                // doesn't sit on a fresh keystroke.
                                setParseResult(null);
                                onChange(e.currentTarget.value === '' ? undefined : e.currentTarget.value);
                            }, onBlur: onBlur })] }), _jsx("p", { id: helpId, className: `phone-field-help phone-field-help-${helpTone}`, "data-phone-help": helpTone, children: helpMessage })] })) }));
}
//# sourceMappingURL=Phone.js.map